import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { KeyValue } from '@angular/common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Step3Model } from 'src/app/chatstyle/models/onboarding.model';
import { getBoardTerminology, getPsaSupportedTicketFields } from 'src/app/chatstyle/models/psa-configurations';
import { FieldType } from 'src/app/shared/dynamic-forms/enums/field-type';
import { AuthService } from 'src/app/auth/auth/auth.service';
import { AppConfigService } from 'src/app/shared/appconfig/appconfig.service';
import { ConnectorDefinition } from 'src/app/chatstyle/models/connector.model';

export interface PsaCustomizationState {
  availableBoards$: BehaviorSubject<KeyValue<string, string>[]>;
  availableSources$: BehaviorSubject<KeyValue<string, string>[]>;
  currentStatusOptions$: BehaviorSubject<KeyValue<string, string>[]>;
  currentTypeOptions$: BehaviorSubject<KeyValue<string, string>[]>;
  availablePriorities$: BehaviorSubject<KeyValue<string, string>[]>;
  // Time entry (Autotask only)
  availableResources$: BehaviorSubject<KeyValue<string, string>[]>;
  availableRoles$: BehaviorSubject<KeyValue<string, string>[]>;
}

/**
 * CHAT-964: copy for the Enterprise note-mode switch. The three states say what the setting does,
 * what unlocks it, and what turns it on, so the reason it is disabled is always real text rather
 * than only a greyed-out control.
 */
const NOTE_MODE_DESCRIPTION =
  'Records the session as an internal Autotask note instead of a time entry, so billable hours and ' +
  'technician utilization are untouched. Clients never see the note.';

const NOTE_MODE_TIER_DESCRIPTION =
  'This feature is available on the Enterprise tier. Records chat time as an internal Autotask note ' +
  'instead of a time entry.';

const NOTE_MODE_REQUIRES_TIME_ENTRIES_DESCRIPTION =
  'Turn on automatic time entries to record chat time as an internal Autotask note instead.';

@Injectable({
  providedIn: 'root'
})
export class PsaCustomizationService {

  private static readonly FALLBACK_OPTIONS = {
    BOARDS: [
      { key: 'Help Desk', value: 'helpdesk' },
      { key: 'Incidents', value: 'incidents' },
      { key: 'Maintenance', value: 'maintenance' },
      { key: 'Projects', value: 'projects' },
      { key: 'Sales Support', value: 'sales' }
    ],
    STATUSES: [
      { key: 'Assigned', value: 'assigned' },
      { key: 'In Progress', value: 'in-progress' },
      { key: 'New', value: 'new' },
      { key: 'Open', value: 'open' }
    ],
    TYPES: [
      { key: '<PSA Default>', value: '' }
    ],
    PRIORITIES: [
      { key: 'High', value: 'high' },
      { key: 'Medium', value: 'medium' },
      { key: 'Low', value: 'low' }
    ],
    SOURCES: [
      { key: 'CloudRadial Chat', value: 'cloudradial-chat' },
      { key: 'Email', value: 'email' },
      { key: 'Phone', value: 'phone' },
      { key: 'Portal', value: 'portal' }
    ]
  } as const;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Create a new state object for PSA customization dropdowns
   */
  createState(): PsaCustomizationState {
    return {
      availableBoards$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableSources$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      currentStatusOptions$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      currentTypeOptions$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availablePriorities$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableResources$: new BehaviorSubject<KeyValue<string, string>[]>([]),
      availableRoles$: new BehaviorSubject<KeyValue<string, string>[]>([])
    };
  }

  /**
   * Complete and clean up the state observables
   */
  destroyState(state: PsaCustomizationState): void {
    state.availableBoards$.complete();
    state.availableSources$.complete();
    state.currentStatusOptions$.complete();
    state.currentTypeOptions$.complete();
    state.availablePriorities$.complete();
    state.availableResources$.complete();
    state.availableRoles$.complete();
  }

  /**
   * Check if the PSA is Syncro (which has all optional fields)
   */
  isSyncroPsa(psaId: string): boolean {
    return psaId?.toLowerCase() === 'syncromsp';
  }

  /**
   * Zendesk is internal to CloudRadial and has no per-PSA Function App: its Ticket Defaults data is served
   * by the WebApi (`connectors/{id}/data`), and its group and status options store the Zendesk value (the
   * numeric group id, the raw status), not the display name. CHAT-1214.
   */
  isZendeskPsa(psaId: string): boolean {
    return psaId?.toLowerCase() === 'zendesk';
  }

  /**
   * Get PSA-specific terminology for boards/queues
   */
  getBoardTerminologyLabel(psaId: string): string {
    switch (psaId?.toLowerCase()) {
      case 'autotask': return 'Queues';
      case 'bms': return 'Queues';
      case 'cwmanage': return 'Board';
      case 'freshdesk': return 'Group';
      case 'zendesk': return 'Group';
      case 'halo': return 'Board';
      case 'syncromsp': return 'Ticket List';
      default: return 'Board';
    }
  }

  /**
   * Build form fields for PSA Customization
   */
  buildFormFields(
    state: PsaCustomizationState,
    psaId: string,
    psaName: string,
    onBoardChange: (boardValue: string) => void,
    allFieldsOptional: boolean = false,
    onResourceChange: (resourceId: string) => void = () => {},
    showTimeEntrySettings: boolean = true,
    disableTimeEntrySettings: boolean = false,
    disableNoteModeSetting: boolean = true
  ): FormlyFieldConfig[] {
    const isSyncro = this.isSyncroPsa(psaId);
    const boardLabel = this.getBoardTerminologyLabel(psaId);
    const support = getPsaSupportedTicketFields(psaId);

    const isAutotask = psaId?.toLowerCase() === 'autotask';

    // Only render the dropdowns the PSA actually supports. Fields a PSA has no concept of
    // (e.g. Syncro has no board/queue and no ticket source) are omitted rather than shown
    // with meaningless fallback values. See CHAT-426.
    const ticketFields: FormlyFieldConfig[] = [];

    if (support.board) {
      ticketFields.push({
        key: 'defaultBoard',
        type: FieldType.Select,
        props: {
          label: `Default ${boardLabel}`,
          placeholder: `<Select ${boardLabel}>`,
          options: state.availableBoards$.asObservable(),
          valueProp: 'value',
          labelProp: 'key',
          description: 'Where new tickets will be created by default',
          required: !isSyncro && !allFieldsOptional,
          attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
        },
        className: 'col-md-6 col-sm-12',
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            field.formControl?.valueChanges.subscribe((boardValue: string) => {
              onBoardChange(boardValue);
            });
          }
        },
      });
    }

    if (support.source) {
      ticketFields.push({
        key: 'ticketSource',
        type: FieldType.Select,
        props: {
          label: 'Ticket Source',
          placeholder: '<Select Source>',
          options: state.availableSources$.asObservable(),
          valueProp: 'value',
          labelProp: 'key',
          description: 'This will be recorded in tickets for reporting purposes',
          attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
        },
        className: 'col-md-6 col-sm-12',
      });
    }

    if (support.type) {
      ticketFields.push({
        key: 'defaultTicketType',
        type: FieldType.Select,
        props: {
          label: 'Ticket Type',
          placeholder: '<PSA Default>',
          options: state.currentTypeOptions$.asObservable(),
          valueProp: 'value',
          labelProp: 'key',
          description: 'Type of ticket to create',
          attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
        },
        className: 'col-md-4 col-sm-12',
      });
    }

    if (support.status) {
      ticketFields.push({
        key: 'defaultStatus',
        type: FieldType.Select,
        props: {
          label: 'Ticket Status',
          placeholder: isSyncro ? '<PSA Default>' : '<Select Status>',
          options: state.currentStatusOptions$.asObservable(),
          valueProp: 'value',
          labelProp: 'key',
          description: 'Initial status for new tickets',
          required: !isSyncro && !allFieldsOptional,
          attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
        },
        className: 'col-md-4 col-sm-12',
      });
    }

    if (support.priority) {
      ticketFields.push({
        key: 'defaultPriority',
        type: FieldType.Select,
        props: {
          label: 'Ticket Priority',
          placeholder: isSyncro ? '<PSA Default>' : '<Select Priority>',
          options: state.availablePriorities$.asObservable(),
          valueProp: 'value',
          labelProp: 'key',
          description: 'Priority level for new tickets',
          required: !isSyncro && !allFieldsOptional,
          attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
        },
        className: 'col-md-4 col-sm-12',
      });
    }

    const fields: FormlyFieldConfig[] = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: ticketFields,
      },
    ];

    // Add time entry settings for Autotask (Pro+ only)
    if (isAutotask && showTimeEntrySettings) {
      fields.push({
        fieldGroupClassName: 'row mt-4',
        fieldGroup: [
          {
            // CHAT-964: styling moved out of an inline `style` attribute, which Angular strips when
            // it sanitizes this HTML — the heading was rendering as an unstyled <h6> with no divider,
            // and its hardcoded #edebe9 rule was invisible on dark. See .psa-formly-section-heading.
            template: '<h6 class="psa-formly-section-heading">Time Entry Settings</h6>',
            className: 'col-12',
          },
          {
            key: 'timeEntryEnabled',
            type: FieldType.Checkbox,
            props: {
              label: 'Enable automatic time entries',
              description: disableTimeEntrySettings
                ? 'This feature is available on Pro and Enterprise tiers'
                : 'Automatically create time entries on Autotask tickets for chat sessions',
              disabled: disableTimeEntrySettings,
            },
            className: 'col-12 mb-2',
          },
          {
            key: 'timeEntryDefaultResourceID',
            type: FieldType.Select,
            props: {
              label: 'Default Resource',
              placeholder: '<Select Resource>',
              options: state.availableResources$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'The resource (technician) to attribute time entries to',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
            expressions: {
              'props.required': 'model.timeEntryEnabled',
              'props.disabled': '!model.timeEntryEnabled',
            },
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                field.formControl?.valueChanges.subscribe((resourceId: string) => {
                  onResourceChange(resourceId);
                });
              }
            },
          },
          {
            key: 'timeEntryDefaultRoleID',
            type: FieldType.Select,
            props: {
              label: 'Default Role',
              placeholder: '<Select Role>',
              options: state.availableRoles$.asObservable(),
              valueProp: 'value',
              labelProp: 'key',
              description: 'The role for the resource on time entries',
              attributes: { autocomplete: 'off', 'data-lpignore': 'true' },
            },
            className: 'col-md-6 col-sm-12',
            expressions: {
              // CHAT-964: also disabled in note mode. An Autotask ticket note has a resource but
              // no role, so the field would be collected and then silently ignored.
              'props.disabled': (field: FormlyFieldConfig) =>
                !field.model?.timeEntryEnabled || !!field.model?.timeEntryNoteMode,
              'props.description': (field: FormlyFieldConfig) =>
                field.model?.timeEntryEnabled && field.model?.timeEntryNoteMode
                  ? 'Not used while chat time is recorded as a note — a ticket note has a resource but no role'
                  : 'The role for the resource on time entries',
            },
          },
          {
            // CHAT-964: the Enterprise note-mode switch. Follows the section's existing tier-gating
            // pattern — visible and disabled below Enterprise tier and while automatic time entries
            // are off, never removed, the same way the Resource and Role fields beside it behave.
            // On a lower tier that is also what advertises the tier.
            key: 'timeEntryNoteMode',
            type: FieldType.Checkbox,
            props: {
              label: 'Record chat time as a private ticket note',
              description: disableNoteModeSetting
                ? NOTE_MODE_TIER_DESCRIPTION
                : NOTE_MODE_DESCRIPTION,
              disabled: true,
            },
            // field-tier-badge draws the Enterprise chip after the label — the same chip the header
            // wears, from the same tokens. See .field-tier-badge in forms.scss.
            className: 'col-12 mt-2 field-tier-badge',
            expressions: disableNoteModeSetting
              ? {
                  // Below Enterprise the control never enables, whatever else is switched on. The
                  // value still round-trips on save, so a connector that already carries the flag
                  // does not lose it when someone on a lower tier saves the page.
                  'props.disabled': () => true,
                }
              : {
                  'props.disabled': '!model.timeEntryEnabled',
                  'props.description': (field: FormlyFieldConfig) =>
                    field.model?.timeEntryEnabled
                      ? NOTE_MODE_DESCRIPTION
                      : NOTE_MODE_REQUIRES_TIME_ENTRIES_DESCRIPTION,
                },
          },
        ],
      });
    }

    return fields;
  }

  /**
   * Create an empty Step3Model
   */
  createEmptyModel(): Step3Model {
    return {
      defaultBoard: '',
      defaultStatus: '',
      defaultTicketType: '',
      defaultPriority: '',
      ticketSource: '',
      allowedBoards: [],
      enablePriorityMapping: false,
      autoAssign: false,
      notifyCreation: true,
      syncStatus: true,
      timeEntryEnabled: false,
      timeEntryDefaultResourceID: '',
      timeEntryDefaultRoleID: '',
      timeEntryNoteMode: false,
    };
  }

  /**
   * Generic method to transform API responses to KeyValue format
   */
  transformApiResponseToKeyValuePairs(apiResponse: any, storeValue = false): KeyValue<string, string>[] {
    let items: any[] = [];

    if (apiResponse?.data && Array.isArray(apiResponse.data)) {
      items = apiResponse.data;
    } else if (Array.isArray(apiResponse)) {
      items = apiResponse;
    } else {
      return [];
    }

    return items
      .filter((item: any) => (item.Name || item.name) && (item.Value || item.value))
      .map((item: any) => ({
        key: item.Name || item.name,
        // Most PSAs store the display name; Zendesk stores its own value (a group id, a raw status).
        value: storeValue ? (item.Value || item.value) : (item.Name || item.name)
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Call PSA connector API with specified type and optional parameters.
   *
   * Two routes share this method:
   *   - Admin / Connectors edit page: a saved Connector entity exists, served by the per-PSA
   *     Azure Function App at `${connector.baseUrl}/api/data/{userID}/{id}`.
   *   - Onboarding Step 3: the Connector entity is not provisioned until post-Step-4 replay,
   *     so the in-process WebApi endpoint `/onboarding/step3/data` resolves the connector
   *     from saved Step 2 credentials. The decision is driven by whether `connector.id`
   *     is present, so the loaders/transforms/fallbacks below stay shared.
   */
  callConnectorApi(connector: ConnectorDefinition, type: string, parameters?: any): Observable<any> {
    const userId = this.authService.getClaims()?.sub;
    if (!userId) {
      return throwError(() => new Error('User authentication required'));
    }

    const psaId = connector?.connectorDefinitionID;
    const mappedType = getBoardTerminology(type, psaId, connector?.id?.toString() || '');

    const inProcess = !!connector?.id && this.isZendeskPsa(psaId);
    let apiUrl: string;
    if (inProcess) {
      const apiServer = AppConfigService.settings.get('chatstyleapiServer');
      apiUrl = `${apiServer}connectors/${connector.id}/data?type=${mappedType}`;
    } else if (connector?.id) {
      const baseUrl = this.getConnectorBaseUrl(connector);
      if (!baseUrl) {
        return throwError(() => new Error('Connector base URL not configured'));
      }
      apiUrl = `${baseUrl}/api/data/${connector.userID}/${connector.id}/?type=${mappedType}`;
    } else {
      const apiServer = AppConfigService.settings.get('chatstyleapiServer');
      apiUrl = `${apiServer}onboarding/step3/data?type=${mappedType}`;
    }

    if (parameters) {
      Object.keys(parameters).forEach(key => {
        apiUrl += `&${key}=${encodeURIComponent(parameters[key])}`;
      });
    }

    return this.http.get(apiUrl).pipe(
      // The WebApi wraps the list as { data: [...] }; the Function Apps return it bare.
      map((response: unknown) => (inProcess ? ((response as { data?: unknown[] } | null)?.data ?? []) : response)),
      catchError((error: any) => {
        console.error(`Error calling connector API with type ${type}:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the base URL from connector configuration
   * Uses connector's baseUrl or derives from testUrl
   */
  private getConnectorBaseUrl(connector: ConnectorDefinition): string | null {
    if (!connector) {
      return null;
    }

    // Use baseUrl if available
    if (connector.baseUrl?.trim()) {
      return connector.baseUrl.trim();
    }

    // Fallback: derive base URL from testUrl
    if (connector.testUrl?.trim()) {
      try {
        const url = new URL(connector.testUrl.trim());
        return `${url.protocol}//${url.host}`;
      } catch (error) {
        console.error('Error parsing testUrl:', error);
      }
    }

    return null;
  }

  /**
   * Build the query-parameter bag for a lookup call: the board qualifier when present, and
   * `refresh=true` when a forced re-fetch is requested. The per-PSA Function App treats refresh
   * as "bypass the cached reference data, re-fetch from the live PSA, and re-cache it". Returns
   * undefined when there are no params so the request URL stays clean.
   */
  private buildLookupParams(boardName?: string, forceRefresh = false): { [key: string]: string } | undefined {
    const params: { [key: string]: string } = {};
    if (boardName) {
      params['name'] = boardName;
    }
    if (forceRefresh) {
      params['refresh'] = 'true';
    }
    return Object.keys(params).length ? params : undefined;
  }

  /**
   * Load boards/queues from PSA connector
   */
  loadBoards(connector: ConnectorDefinition, state: PsaCustomizationState, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'board', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((metadata: any) => {
        let boards: any[] = [];
        if (metadata?.data) {
          boards = metadata.data;
        } else if (Array.isArray(metadata)) {
          boards = metadata;
        }
        const zendesk = this.isZendeskPsa(connector?.connectorDefinitionID);
        const processedBoards = this.transformApiResponseToKeyValuePairs(boards, zendesk);
        // Use fallback if API returns empty array. Zendesk has no stand-in groups: made-up ones would be saved as ids.
        if (processedBoards.length > 0) {
          state.availableBoards$.next(processedBoards);
        } else {
          state.availableBoards$.next(zendesk ? [] : [...PsaCustomizationService.FALLBACK_OPTIONS.BOARDS]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading boards:', error);
        state.availableBoards$.next(this.isZendeskPsa(connector?.connectorDefinitionID)
          ? []
          : [...PsaCustomizationService.FALLBACK_OPTIONS.BOARDS]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket statuses from PSA connector based on selected board
   */
  loadStatuses(connector: ConnectorDefinition, state: PsaCustomizationState, boardName?: string, alwaysLoad = false, forceRefresh = false): Observable<any> {
    // Board-based PSAs scope statuses to a board, so without one there is nothing to load.
    // Boardless PSAs (e.g. Syncro) pass alwaysLoad=true to fetch the flat status list directly.
    if (!boardName && !alwaysLoad) {
      state.currentStatusOptions$.next([]);
      return of(null);
    }

    const params = this.buildLookupParams(boardName, forceRefresh);
    return this.callConnectorApi(connector, 'status', params).pipe(
      tap((statuses: any) => {
        const zendesk = this.isZendeskPsa(connector?.connectorDefinitionID);
        const fallback = zendesk ? [] : [...PsaCustomizationService.FALLBACK_OPTIONS.STATUSES];
        if (statuses && Array.isArray(statuses)) {
          const processedStatuses = this.transformApiResponseToKeyValuePairs(statuses, zendesk);
          // Use fallback if API returns empty array
          state.currentStatusOptions$.next(processedStatuses.length > 0 ? processedStatuses : fallback);
        } else {
          state.currentStatusOptions$.next(fallback);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading statuses:', error);
        state.currentStatusOptions$.next(this.isZendeskPsa(connector?.connectorDefinitionID)
          ? []
          : [...PsaCustomizationService.FALLBACK_OPTIONS.STATUSES]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket sources from PSA connector
   */
  loadSources(connector: ConnectorDefinition, state: PsaCustomizationState, model: Step3Model, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'source', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((sources: any) => {
        if (sources && Array.isArray(sources)) {
          const processedSources = this.transformApiResponseToKeyValuePairs(sources);
          if (!model.ticketSource && processedSources.length > 0) {
            const defaultSource = processedSources.find(s =>
              s.key.toLowerCase().includes('cloudradial') ||
              s.key.toLowerCase().includes('chat')
            );
            model.ticketSource = defaultSource ? defaultSource.value : processedSources[0].value;
          }
          state.availableSources$.next(processedSources);
        } else {
          if (!model.ticketSource) {
            model.ticketSource = 'cloudradial-chat';
          }
          state.availableSources$.next([...PsaCustomizationService.FALLBACK_OPTIONS.SOURCES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading sources:', error);
        if (!model.ticketSource) {
          model.ticketSource = 'cloudradial-chat';
        }
        state.availableSources$.next([...PsaCustomizationService.FALLBACK_OPTIONS.SOURCES]);
        return of(null);
      })
    );
  }

  /**
   * Load ticket types from PSA connector based on selected board
   */
  loadTypes(connector: ConnectorDefinition, state: PsaCustomizationState, boardName?: string, alwaysLoad = false, forceRefresh = false): Observable<any> {
    // Board-based PSAs scope types to a board, so without one we show only the PSA-default
    // placeholder. Boardless PSAs (e.g. Syncro) pass alwaysLoad=true to fetch types directly.
    if (!boardName && !alwaysLoad) {
      state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
      return of(null);
    }

    const params = this.buildLookupParams(boardName, forceRefresh);
    return this.callConnectorApi(connector, 'type', params).pipe(
      tap((types: any) => {
        if (types && Array.isArray(types)) {
          const processedTypes = this.transformApiResponseToKeyValuePairs(types);
          // Use fallback if API returns empty array
          if (processedTypes.length > 0) {
            state.currentTypeOptions$.next(processedTypes);
          } else {
            state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
          }
        } else {
          state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading types:', error);
        state.currentTypeOptions$.next([...PsaCustomizationService.FALLBACK_OPTIONS.TYPES]);
        return of(null);
      })
    );
  }

  /**
   * Load priorities from PSA connector
   */
  /**
   * Transform API response to KeyValue pairs using the actual Value field (not Name).
   * Used for resources/roles where the value is a numeric ID that must be preserved.
   */
  private transformApiResponseWithIds(apiResponse: any): KeyValue<string, string>[] {
    let items: any[] = [];

    if (apiResponse?.data && Array.isArray(apiResponse.data)) {
      items = apiResponse.data;
    } else if (Array.isArray(apiResponse)) {
      items = apiResponse;
    } else {
      return [];
    }

    return items
      .filter((item: any) => (item.Name ?? item.name) != null && (item.Value ?? item.value) != null)
      .map((item: any) => ({
        key: item.Name ?? item.name,
        value: String(item.Value ?? item.value)
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Load resources from Autotask connector (for time entry config)
   */
  loadResources(connector: ConnectorDefinition, state: PsaCustomizationState, forceRefresh = false): Observable<any> {
    return this.callConnectorApi(connector, 'resource', forceRefresh ? { refresh: 'true' } : undefined).pipe(
      tap((response: any) => {
        const resources = this.transformApiResponseWithIds(response);
        state.availableResources$.next(resources);
      }),
      catchError((error: any) => {
        console.error('Error loading resources:', error);
        state.availableResources$.next([]);
        return of(null);
      })
    );
  }

  /**
   * Load roles for a specific resource from Autotask connector (for time entry config)
   */
  loadRoles(connector: ConnectorDefinition, state: PsaCustomizationState, resourceId: string): Observable<any> {
    if (!resourceId) {
      state.availableRoles$.next([]);
      return of(null);
    }

    return this.callConnectorApi(connector, 'role', { name: resourceId }).pipe(
      tap((response: any) => {
        const roles = this.transformApiResponseWithIds(response);
        state.availableRoles$.next(roles);
      }),
      catchError((error: any) => {
        console.error('Error loading roles:', error);
        state.availableRoles$.next([]);
        return of(null);
      })
    );
  }

  loadPriorities(connector: ConnectorDefinition, state: PsaCustomizationState, psaId: string, forceRefresh = false, boardName?: string): Observable<any> {
    const isSyncro = this.isSyncroPsa(psaId);
    const params = boardName
      ? { name: boardName, ...(forceRefresh ? { refresh: 'true' } : {}) }
      : (forceRefresh ? { refresh: 'true' } : undefined);
    return this.callConnectorApi(connector, 'priority', params).pipe(
      tap((priorities: any) => {
        if (priorities && Array.isArray(priorities)) {
          const processedPriorities = this.transformApiResponseToKeyValuePairs(priorities);
          const filteredPriorities = processedPriorities.filter(p =>
            !p.key.toLowerCase().includes('psa default')
          );
          // Use fallback if API returns empty array
          if (filteredPriorities.length > 0) {
            const priorityOptions = isSyncro
              ? [{ key: '<PSA Default>', value: '' }, ...filteredPriorities]
              : filteredPriorities;
            state.availablePriorities$.next(priorityOptions);
          } else {
            state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
          }
        } else {
          state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
        }
      }),
      catchError((error: any) => {
        console.error('Error loading priorities:', error);
        state.availablePriorities$.next([...PsaCustomizationService.FALLBACK_OPTIONS.PRIORITIES]);
        return of(null);
      })
    );
  }
}
